/**
 * Admin route protection component.
 *
 * Wraps admin routes to ensure only authenticated users with admin privileges
 * can access them. Displays loading state during authentication check, redirects
 * unauthenticated users to sign-in, and shows access denied for non-admin users.
 *
 * @module AdminRoute
 */

import { Navigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ROUTES } from '@/lib/routeConstants';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Shield, AlertTriangle, Loader2 } from 'lucide-react';

interface AdminRouteProps {
  readonly children: React.ReactNode;
}

export default function AdminRoute({ children }: AdminRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
          <p className="font-ui text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Redirect to sign in page with return url
    return <Navigate to={ROUTES.SIGN_IN} state={{ from: location }} replace />;
  }

  if (user?.userType !== 'admin') {
    // User is authenticated but not admin - show access denied
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-destructive/10 rounded-full w-fit">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
            <CardTitle className="font-ui text-2xl text-destructive">
              Access Denied
            </CardTitle>
            <CardDescription className="font-ui">
              You do not have permission to access this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="font-ui text-muted-foreground">
              Admin privileges are required to access this section.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Shield className="h-4 w-4" />
              <span className="font-ui">Administrator access required</span>
            </div>
            <div className="pt-4">
              <Button asChild>
                <Link to={ROUTES.DASHBOARD}>Return to Dashboard</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
